import { stagger, type Variants } from "framer-motion";
import { duration, ease, interval, travel } from "./tokens";

export type Direction = "up" | "down" | "left" | "right" | "none";

export interface FadeOptions {
  dir?: Direction;
  distance?: number;
  duration?: number;
  delay?: number;
}

const offset = (dir: Direction, d: number) => {
  if (d === 0) return {};
  switch (dir) {
    case "up": return { y: d };
    case "down": return { y: -d };
    case "left": return { x: d };
    case "right": return { x: -d };
    default: return {};
  }
};

/**
 * Scroll-triggered fade. `reduce` collapses travel to 0 and shortens the tween —
 * the element still cross-fades, it just never moves.
 */
export function fade(o: FadeOptions = {}, reduce = false): Variants {
  const {
    dir = "up",
    distance = travel.md,
    duration: dur = duration.base,
    delay = 0,
  } = o;

  return {
    hidden: { opacity: 0, ...offset(dir, reduce ? 0 : distance) },
    visible: {
      opacity: 1,
      x: 0,
      y: 0,
      transition: {
        duration: reduce ? duration.reduced : dur,
        delay: reduce ? 0 : delay,
        ease: ease.entrance,
      },
    },
  };
}

export interface StaggerOptions {
  interval?: number;
  delay?: number;
  from?: number | "first" | "last" | "center";
}

/**
 * Container variants. `hidden`/`visible` are intentionally EMPTY targets: the
 * container animates nothing itself (no extra stacking context, no double-fade
 * over its children) and exists purely to orchestrate.
 *
 * NOTE: `staggerChildren` is deprecated in v13. A numeric `delayChildren` does
 * NOT combine with the `stagger()` function form — when a function is passed the
 * numeric base delay is skipped — so the start delay lives inside stagger().
 */
export function staggerContainer(o: StaggerOptions = {}, reduce = false): Variants {
  const { interval: i = interval.base, delay = 0, from = "first" } = o;
  return {
    hidden: {},
    visible: {
      transition: reduce
        ? { delayChildren: 0 }
        : { delayChildren: stagger(i, { startDelay: delay, from }) },
    },
  };
}

/** Hover/tap. Transform + opacity only — never box-shadow, width, or top/left. */
export function interactive(
  { lift = 3, scale = 1.03, press = 0.97 } = {},
  reduce = false,
): Variants {
  if (reduce) {
    return { rest: { opacity: 1 }, hover: { opacity: 0.85 }, press: { opacity: 0.7 } };
  }
  return {
    rest: { y: 0, scale: 1, transition: { duration: duration.hover, ease: ease.hover } },
    hover: { y: -lift, scale, transition: { duration: duration.hover, ease: ease.hover } },
    press: { y: 0, scale: press, transition: { duration: duration.press, ease: ease.hover } },
  };
}
